// Beschriftung, Bedienelement, Hinweis, Fehler — in dieser Reihenfolge und immer verbunden. Das
// Bedienelement kommt als Kind herein und trägt die id, die `htmlFor` nennt; so bleibt der Baustein
// frei von der Frage, ob dort ein input, ein select oder ein textarea steht.
import { cloneElement, isValidElement, type ReactElement, type ReactNode } from 'react'
import { Info } from './Info'
import './bausteine.css'

export interface FeldProps {
  label: string
  htmlFor: string
  /** Die deutsche Meldung — aus zod oder wörtlich vom Server. */
  fehler?: string
  hinweis?: string
  /** Eine Erklärung, die nicht dauerhaft unter dem Feld stehen muss: sie erscheint hinter einem
   *  i-Symbol neben der Beschriftung und bleibt die Beschreibung des Bedienelements. */
  info?: string
  children: ReactNode
}

export function Feld({ label, htmlFor, fehler, hinweis, info, children }: FeldProps): ReactElement {
  const hinweisId = `${htmlFor}-hinweis`
  const infoId = `${htmlFor}-info`
  const fehlerId = `${htmlFor}-fehler`
  const beschrieben = [
    hinweis === undefined ? null : hinweisId,
    info === undefined ? null : infoId,
    fehler === undefined ? null : fehlerId,
  ]
    .filter((id): id is string => id !== null)
    .join(' ')

  // aria-invalid und aria-describedby gehören an das Bedienelement, nicht an die Hülle — deshalb
  // wird das Kind ergänzt statt umwickelt. Ist es kein Element (Text, Fragment), bleibt es, wie es ist.
  const kind = isValidElement(children)
    ? cloneElement(children as ReactElement<Record<string, unknown>>, {
        'aria-invalid': fehler === undefined ? undefined : true,
        'aria-describedby': beschrieben === '' ? undefined : beschrieben,
      })
    : children

  return (
    <p className="feld">
      {info === undefined ? (
        <label htmlFor={htmlFor}>{label}</label>
      ) : (
        <span className="feld-kopf">
          <label htmlFor={htmlFor}>{label}</label>
          <Info titel={label} id={infoId}>{info}</Info>
        </span>
      )}
      {kind}
      {hinweis === undefined ? null : (
        <span id={hinweisId} className="feld-hinweis">{hinweis}</span>
      )}
      {fehler === undefined ? null : (
        <span id={fehlerId} role="alert" className="feld-fehler">{fehler}</span>
      )}
    </p>
  )
}
